import React from 'react';
import { FaArrowLeft, FaArrowRight } from 'react-icons/fa';
import { SUCCESS_STORY_CARDS } from '../../data/successStoryCards';
import { startCard } from './startPageTokens';
import { StoryResultsDisplay } from './SuccessStoryStats';

const CAROUSEL_THEMES = {
  // `grid`: when the two stories go side by side. Each card needs ~400px for the
  // Before/Now values to stay big on one line, so pages with a sidebar wait for xl.
  start: {
    grid: 'xl:grid-cols-2 xl:gap-8',
    card: `${startCard} p-5 md:p-6 flex flex-col`,
    title: 'text-2xl font-bold text-gray-800 dark:text-white text-center mb-4',
    meta: 'text-base text-gray-600 dark:text-gray-300',
    tabActive: 'bg-red-600 text-white border-red-600',
    tabInactive:
      'bg-gray-100 dark:bg-gray-700/50 text-gray-700 dark:text-gray-300 border-gray-200 dark:border-gray-600 hover:bg-gray-200 dark:hover:bg-gray-600',
    arrow: 'shadow-sm'
  },
  // Always-dark glass, for the public /welcome popups. Render inside a `dark`
  // wrapper so the nested `dark:` utilities below resolve regardless of theme.
  welcome: {
    grid: 'lg:grid-cols-2 lg:gap-8',
    card:
      'rounded-2xl border border-white/10 bg-zinc-900/60 backdrop-blur-xl shadow-[0_18px_50px_-24px_rgba(0,0,0,0.9)] p-5 md:p-6 flex flex-col',
    title: 'text-xl md:text-2xl font-bold text-white text-center mb-4',
    meta: 'text-sm md:text-base text-zinc-300',
    tabActive:
      'bg-gradient-to-r from-red-600 to-rose-500 text-white border-transparent shadow-lg shadow-red-900/40',
    tabInactive:
      'bg-white/[0.04] text-zinc-400 border-white/10 hover:bg-white/[0.09] hover:text-white',
    arrow: 'shadow-lg shadow-red-900/40'
  },
  crm: {
    grid: 'xl:grid-cols-2 xl:gap-8',
    card:
      'bg-white/20 dark:bg-white/10 backdrop-blur-md rounded-xl p-5 md:p-6 flex flex-col shadow-lg border border-white/30 dark:border-white/20',
    title: 'text-2xl font-bold text-gray-900 dark:text-white text-center mb-4',
    meta: 'text-base text-gray-800 dark:text-gray-200',
    tabActive: 'bg-red-600 text-white border-red-600',
    tabInactive:
      'bg-white/30 dark:bg-white/10 text-gray-900 dark:text-gray-200 border-white/40 dark:border-white/20 hover:bg-white/40 dark:hover:bg-white/20',
    arrow: 'shadow-sm'
  }
};

/**
 * Two-up success story carousel with revenue tabs (shared by Start + CRM assessment).
 */
const SuccessStoriesCarousel = ({
  theme = 'start',
  successStoriesIndex,
  setSuccessStoriesIndex,
  selectedRevenueRange,
  setSelectedRevenueRange,
  cards = SUCCESS_STORY_CARDS
}) => {
  const styles = CAROUSEL_THEMES[theme] || CAROUSEL_THEMES.start;
  const filteredCards = cards.filter((card) => card.revenueRange === selectedRevenueRange);
  const revenueRanges = [...new Set(cards.map((card) => card.revenueRange).filter(Boolean))];
  const cardsToShow = 2;
  const maxIndex = Math.max(0, filteredCards.length - cardsToShow);
  const visibleCards = filteredCards.slice(successStoriesIndex, successStoriesIndex + cardsToShow);

  return (
    <div className="relative mb-12">
      {revenueRanges.length > 0 && (
        <div className="mb-8">
          <div className="flex justify-center gap-4 mb-6 flex-wrap">
            {revenueRanges.map((range) => (
              <button
                key={range}
                type="button"
                onClick={() => setSelectedRevenueRange(range)}
                className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors border ${
                  selectedRevenueRange === range ? styles.tabActive : styles.tabInactive
                }`}
              >
                ${range}
              </button>
            ))}
          </div>
        </div>
      )}

      {filteredCards.length > cardsToShow && (
        <button
          type="button"
          onClick={() => setSuccessStoriesIndex((prev) => (prev === 0 ? maxIndex : prev - 2))}
          className={`absolute left-0 top-1/2 -translate-y-1/2 -translate-x-12 z-10 p-3 rounded-full bg-red-600 hover:bg-red-700 text-white transition-all opacity-100 ${styles.arrow}`}
          aria-label="Previous stories"
        >
          <FaArrowLeft className="w-5 h-5" />
        </button>
      )}

      <div className={`grid grid-cols-1 gap-6 transition-all duration-500 ${styles.grid}`}>
        {visibleCards.map((card, index) => (
          <div key={`${successStoriesIndex}-${index}-${card.title}`} className={styles.card}>
            <h4 className={styles.title}>{card.title}</h4>
            {(card.location || card.time) && (
              <div className={`flex justify-center gap-6 md:gap-8 mb-6 flex-wrap ${styles.meta}`}>
                {card.location && (
                  <div className="flex items-center gap-2">
                    <span className="text-xl" aria-hidden>
                      📍
                    </span>
                    <span className="font-medium">{card.location}</span>
                  </div>
                )}
                {card.time && (
                  <div className="flex items-center gap-2">
                    <span className="text-xl" aria-hidden>
                      🕐
                    </span>
                    <span className="font-medium">Time Frame: {card.time}</span>
                  </div>
                )}
              </div>
            )}

            {card.problem?.length > 0 && (
              <div className="bg-red-50 dark:bg-red-900/20 rounded-lg p-4 mb-4">
                <h5 className="text-sm font-semibold text-gray-800 dark:text-white mb-3 uppercase text-center">
                  Before BBA
                </h5>
                <ul className="space-y-2 list-disc list-inside">
                  {card.problem.map((item, idx) => (
                    <li key={idx} className="text-sm leading-relaxed text-gray-700 dark:text-gray-200">
                      {item}
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {card.fix?.length > 0 && (
              <div className="bg-amber-50 dark:bg-amber-900/20 rounded-lg p-4 mb-4">
                <h5 className="text-sm font-semibold text-gray-800 dark:text-white mb-3 uppercase text-center">
                  Key Implementations
                </h5>
                <ul className="space-y-2">
                  {card.fix.map((item, idx) => (
                    <li
                      key={idx}
                      className="text-sm text-amber-800 dark:text-amber-200 leading-relaxed flex items-start gap-2"
                    >
                      <span className="text-amber-600 dark:text-amber-400 shrink-0">★</span>
                      <span>{item}</span>
                    </li>
                  ))}
                </ul>
              </div>
            )}

            <StoryResultsDisplay result={card.result} />
          </div>
        ))}
      </div>

      {filteredCards.length > cardsToShow && (
        <button
          type="button"
          onClick={() => setSuccessStoriesIndex((prev) => (prev >= maxIndex ? 0 : prev + 2))}
          className={`absolute right-0 top-1/2 -translate-y-1/2 translate-x-12 z-10 p-3 rounded-full bg-red-600 hover:bg-red-700 text-white transition-all opacity-100 ${styles.arrow}`}
          aria-label="Next stories"
        >
          <FaArrowRight className="w-5 h-5" />
        </button>
      )}
    </div>
  );
};

export default SuccessStoriesCarousel;
